import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import { PageHeader, FilterCard, Field, SimpleSelect } from "@/components/common";
import { MONTHS } from "@/lib/helpers";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BellRing, CheckCircle2, Bell } from "lucide-react";

export default function Notifikasi() {
  const { indikators, tahuns } = useMeta();
  const [indikator, setIndikator] = useState("");
  const [tahun, setTahun] = useState(String(new Date().getFullYear()));
  const [bulan, setBulan] = useState(String(new Date().getMonth() + 1));
  const [data, setData] = useState(null);

  useEffect(() => { if (!indikator && indikators.length) setIndikator(indikators[0].kode); }, [indikators]);
  useEffect(() => {
    if (!indikator) return;
    api.get(`/notifikasi?indikator=${encodeURIComponent(indikator)}&tahun=${tahun}&bulan=${bulan}`).then((r) => setData(r.data));
  }, [indikator, tahun, bulan]);

  const pct = data && data.total_poliklinik ? Math.round((data.sudah_upload / data.total_poliklinik) * 100) : 0;
  const lengkap = data && data.belum_upload.length === 0 && data.total_poliklinik > 0;

  return (
    <div data-testid="notifikasi-page">
      <PageHeader icon={BellRing} title="Notifikasi Kelengkapan Data" subtitle="Daftar poliklinik yang belum ada datanya untuk bulan berjalan." />

      <FilterCard>
        <Field label="Indikator">
          <SimpleSelect testId="notif-indikator-select" value={indikator} onChange={setIndikator}
            options={indikators.map((i) => ({ label: i.kode, value: i.kode }))} className="w-[220px]" />
        </Field>
        <Field label="Bulan">
          <SimpleSelect testId="notif-bulan-select" value={bulan} onChange={setBulan}
            options={MONTHS.map((m) => ({ label: m.nama, value: m.n }))} className="w-[160px]" />
        </Field>
        <Field label="Tahun">
          <SimpleSelect testId="notif-tahun-select" value={tahun} onChange={setTahun}
            options={[...new Set([...tahuns, Number(tahun)])].sort((a, b) => b - a).map((t) => ({ label: String(t), value: t }))} className="w-[120px]" />
        </Field>
      </FilterCard>

      {data && (
        <Card className="border-slate-200 shadow-sm mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm text-slate-500">Kelengkapan {MONTHS[bulan - 1]?.nama} {tahun}</div>
                <div className="text-2xl font-extrabold text-slate-900 tabular-nums" data-testid="notif-progress-text">
                  {data.sudah_upload} / {data.total_poliklinik} poliklinik
                </div>
              </div>
              {lengkap ? (
                <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 className="w-4 h-4 mr-1" /> Sudah Lengkap</Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-800">Belum Lengkap</Badge>
              )}
            </div>
            <Progress value={pct} className="h-3" data-testid="notif-progress-bar" />
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-lg">Poliklinik Belum Upload ({data?.belum_upload.length || 0})</CardTitle></CardHeader>
        <CardContent>
          {lengkap ? (
            <div className="flex flex-col items-center py-10 text-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mb-3" />
              <p className="font-semibold text-slate-700">Semua poliklinik sudah mengunggah data 🎉</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data?.belum_upload.map((p) => (
                <div key={p.poliklinik_id} className="flex items-center justify-between border border-slate-200 rounded-lg px-4 py-3" data-testid={`belum-${p.poliklinik_id}`}>
                  <div>
                    <div className="font-medium text-slate-800">{p.nama_poliklinik}</div>
                    <div className="text-xs text-slate-500">{p.nama_departemen}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => toast.success(`Pengingat dikirim ke ${p.nama_poliklinik}`)} data-testid={`notifikasi-send-reminder-button-${p.poliklinik_id}`}>
                    <Bell className="w-3.5 h-3.5 mr-1" /> Ingatkan
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
